/**
 * Availability Helpers Composable
 *
 * Shared UI helpers for availability checking functionality.
 * Provides consistent status messages, alert types, and retry logic
 * across Offer Page and Checkout Page.
 *
 * @example
 * ```typescript
 * const { getStatusMessage, getStatusType, canRetry } = useAvailabilityHelpers();
 *
 * const message = getStatusMessage(712, null); // "Available for immediate booking"
 * const alertType = getStatusType(712, false); // "success"
 * const canRetryRequest = canRetry('timeout'); // true
 * ```
 */

import { $t } from '@/composables/translations/useTranslate';
import {
  getCodeMeta,
  isAvailableCode,
  getFlowType,
  getStatusTypeForCode,
  BOOKING_SUCCESS_CODES,
  type StatusType,
} from '@/constants/davinci/returnCodes';

export type AlertType = StatusType;

/**
 * Composable for availability UI helpers
 */
export function useAvailabilityHelpers() {
  /**
   * Get user-friendly status message for availability result
   * Uses centralized registry for consistent messaging across all components.
   *
   * @param code - DaVinci return code (712, 707, 733, 734, 923, etc.)
   * @param errorMessage - Optional error message
   * @returns Translated status message
   */
  function getStatusMessage(code: number | null, errorMessage: string | null = null): string {
    if (errorMessage) {
      return errorMessage;
    }

    if (code === null) {
      return $t('ibe.availability.checking', 'Checking availability...');
    }

    const meta = getCodeMeta(code);
    if (meta) {
      return $t(meta.translationKey, meta.fallbackMessage);
    }

    // Success codes (from B/BQ/BC actions)
    if (BOOKING_SUCCESS_CODES.FIXED_BOOKING.includes(code)) {
      return $t('ibe.availability.success.confirmed', 'Booking confirmed');
    }
    if (BOOKING_SUCCESS_CODES.REQUEST_BOOKING.includes(code)) {
      return $t('ibe.availability.success.requested', 'Request booking successful');
    }

    // Unavailable / error
    return $t('ibe.availability.unavailable', 'This offer is not available');
  }

  /**
   * Get alert type for status display
   * Uses centralized registry for consistent status types across all components.
   *
   * @param code - DaVinci return code
   * @param hasError - Whether there's an error
   * @param isLoading - Whether check is in progress
   * @returns Alert type for UI rendering
   */
  function getStatusType(
    code: number | null,
    hasError: boolean,
    isLoading: boolean = false
  ): AlertType {
    if (hasError) {
      return 'error';
    }

    if (isLoading) {
      return 'info';
    }

    if (code === null) {
      return 'info';
    }

    if (isAvailableCode(code)) {
      return getStatusTypeForCode(code);
    }

    // Success codes (from B/BQ/BC actions)
    if (
      BOOKING_SUCCESS_CODES.FIXED_BOOKING.includes(code) ||
      BOOKING_SUCCESS_CODES.REQUEST_BOOKING.includes(code) ||
      BOOKING_SUCCESS_CODES.CONFIRMATION.includes(code)
    ) {
      return 'success';
    }

    return 'error';
  }

  /**
   * Check if retry is possible based on error type
   *
   * @param errorMessage - Error message or error type
   * @returns true if retry is recommended
   */
  function canRetry(errorMessage: string | null): boolean {
    if (!errorMessage) return false;

    const lower = errorMessage.toLowerCase();
    return lower.includes('timeout') || lower.includes('network') || lower.includes('connection');
  }

  /**
   * Check if return/back button should be shown
   *
   * @param code - DaVinci return code
   * @param errorMessage - Optional error message
   * @param hasChecked - Whether availability was checked
   * @returns true if return button should be displayed
   */
  function shouldShowReturnButton(
    code: number | null,
    errorMessage: string | null,
    hasChecked: boolean
  ): boolean {
    if (hasChecked && code !== null && !isAvailableCode(code)) {
      return true;
    }

    if (errorMessage && !canRetry(errorMessage)) {
      return true;
    }

    return false;
  }

  /**
   * Get booking flow type based on return code
   * Uses centralized registry for consistent flow type detection.
   *
   * @param code - DaVinci return code
   * @returns Booking flow type or null
   */
  function getBookingFlowType(code: number | null): 'fixed' | 'request' | 'confirmation' | null {
    if (code === null) return null;
    return getFlowType(code) ?? null;
  }

  /**
   * Get icon class name for status
   *
   * @param type - Alert type
   * @returns Icon class name
   */
  function getStatusIconClass(type: AlertType): string {
    switch (type) {
      case 'success':
        return 'icon-checkmark';
      case 'error':
        return 'icon-warning';
      case 'info':
        return 'icon-info';
      case 'warning':
        return 'icon-warning';
      default:
        return 'icon-info';
    }
  }

  return {
    // Status messages
    getStatusMessage,
    getStatusType,
    getStatusIconClass,

    // Action helpers
    canRetry,
    shouldShowReturnButton,

    // Flow type
    getBookingFlowType,
  };
}
